import { useNavigate } from "react-router-dom";
import Datatable from "./common/datatable";
import { useViewIndustryQuery } from "../hooks/industry/industryList";
import { useViewCategory } from "../hooks/categorys/category.showall";
import ActionMenu from "./common/menu";
import { toListArray } from "../utils/parsePaginatedResponse";
import FallbackImage from "./common/fallbackImage";

const ProductData = ({ products, onEdit, onView, onDelete }) => {
  const navigate = useNavigate();

  const handleEdit = (product) => {
    const id = product._id || product.id;
    if (onEdit) {
        onEdit(product);
    } else {
        navigate(`/products/edit/${id}`);
    }
  };

  const handleDelete = (product) => {
    if (onDelete) {
        onDelete(product);
    }
  };

  const handleView = (product) => {
    const id = product._id || product.id;
    if (onView) {
        onView(product);
    } else if (id) {
      navigate(`/products/view/${id}`);
    }
  };

  const { data: industriesData } = useViewIndustryQuery();
  const { data: categoriesData } = useViewCategory();

  const industriesList = toListArray(industriesData, ["industry", "industries"]);
  const categoriesList = toListArray(categoriesData, ["categories", "category"]);

  const getIndustryTitle = (val) => {
    if (!val || (Array.isArray(val) && val.length === 0)) return '-';
    const items = Array.isArray(val) ? val : [val];
    return items.map(item => {
      const id = typeof item === 'string' ? item : item?._id || item?.id;
      const found = industriesList.find(i => i._id === id || i.id === id);
      return found ? found.title : (typeof item === 'string' ? item : item?.title || item?._id || '-');
    }).join(', ');
  };

  const getCategoryTitle = (val) => {
    if (!val || (Array.isArray(val) && val.length === 0)) return '-';
    const items = Array.isArray(val) ? val : [val];
    return items.map(item => {
      const id = typeof item === 'string' ? item : item?._id || item?.id;
      const found = categoriesList.find(c => c._id === id || c.id === id);
      return found ? found.title : (typeof item === 'string' ? item : item?.title || item?._id || '-');
    }).join(', ');
  };

  const toBool = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const s = value.trim().toLowerCase();
      if (["true", "1", "active", "enabled", "yes"].includes(s)) return true;
      if (["false", "0", "inactive", "disabled", "no"].includes(s))
        return false;
    }
    return false;
  };

  // Columns
  const columns = [
    {
      key: "thumbnail_image",
      label: "Thumbnail Image",
      render: (_, row) => {
        const thumb =
          row.thumbnail_image ||
          row.thumbnail ||
          row.image ||
          row.media?.thumbnail ||
          row.media?.thumbnail_image ||
          row.media?.images?.[0];

        if (!thumb) return "-";

        return (
          <FallbackImage
            src={thumb}
            alt="img"
            style={{
              width: 35,
              height: 35,
              borderRadius: "4px",
              objectFit: "cover",
            }}
          />
        );
      },
    },
    { key: "title", label: "Title" },
    { key: "category", label: "Category", render: (value) => getCategoryTitle(value) },
    { key: "industries", label: "Industry", render: (value) => getIndustryTitle(value) },
    {
      key: "status",
      label: "Status",
      render: (_v, row) => {
        const raw =
          typeof row?.isActive !== "undefined" ? row.isActive : row?.status;
        const isActive = toBool(raw);
        return (
          <span
            className={`status-badge ${isActive ? "status-active" : "status-expired"}`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
     {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <ActionMenu 
          row={row}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ),
    },
  ];

  return (
    <>
      {/* Products Table */}
      <Datatable columns={columns} data={products} />
    </>
  );
};

export default ProductData;
